import { z } from 'zod';

import type {
  ListShipmentsRequest,
  ListShipmentsResponse,
  ShipmentListItem,
  ShipmentStatusSummaryItem,
} from './list-shipments.dto.js';
import { ShipmentSourceEvidenceSchema } from './shipments-shared.schema.js';
import { IsoDateTimeSchema, RcScopeTypeSchema, ShipmentStatusSchema } from './shipments-types.js';

const nonNegativeIntSchema = z.number().int().min(0);

const validateDateRange = (value: unknown, ctx: z.RefinementCtx): void => {
  if (!value || typeof value !== 'object') {
    return;
  }
  const from = (value as { from?: string }).from;
  const to = (value as { to?: string }).to;
  if (typeof from !== 'string' || typeof to !== 'string') {
    return;
  }
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (!Number.isNaN(fromMs) && !Number.isNaN(toMs) && fromMs >= toMs) {
    ctx.addIssue({
      code: 'custom',
      message: 'from must be before to.',
      path: ['to'],
    });
  }
};

const ShipmentListItemSchema: z.ZodType<ShipmentListItem> = z
  .object({
    shipmentId: z.uuid(),
    companyId: z.uuid(),
    stationId: z.uuid(),
    rcScopeId: z.uuid(),
    rcScopeType: RcScopeTypeSchema,
    materialId: z.uuid(),
    status: ShipmentStatusSchema,
    rangeStartAt: IsoDateTimeSchema,
    rangeEndAt: IsoDateTimeSchema,
    createdAt: IsoDateTimeSchema,
    departedAt: IsoDateTimeSchema.nullable().optional(),
    ...ShipmentSourceEvidenceSchema.shape,
    voidedAt: IsoDateTimeSchema.nullable(),
    receiverDisplayName: z.string().trim().min(1),
  })
  .strict();

const ShipmentStatusSummaryItemSchema: z.ZodType<ShipmentStatusSummaryItem> = z
  .object({
    status: ShipmentStatusSchema,
    count: nonNegativeIntSchema,
  })
  .strict();

export const ListShipmentsRequestSchema: z.ZodType<ListShipmentsRequest> = z
  .object({
    companyId: z.uuid(),
    rcScopeId: z.uuid().optional(),
    materialId: z.uuid().optional(),
    statuses: z.array(ShipmentStatusSchema).min(1).optional(),
    from: IsoDateTimeSchema.optional(),
    to: IsoDateTimeSchema.optional(),
    includePendingNoReceiveDate: z.boolean().optional(),
  })
  .strict()
  .superRefine(validateDateRange);

export const ListShipmentsResponseSchema: z.ZodType<ListShipmentsResponse> = z
  .object({
    items: z.array(ShipmentListItemSchema),
    summaryByStatus: z.array(ShipmentStatusSummaryItemSchema),
  })
  .strict();
